import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UserInteractionApiService } from '@angular-workspace/services';
// Share feature: commented out for now. Uncomment to re-enable.
// import { CardShareButtonComponent } from '../card-share-button/card-share-button.component';

export interface UsecaseCardData {
  id: string;
  title: string;
  description: string;
  organization?: string;
  tags: string[];
  apiTags?: string[];
  date?: string;
  badge?: string;
  badgeColor?: 'public' | 'private' | 'restricted';
  isNew?: boolean;
  link?: string;
  metrics?: {
    likes?: number;
    downloads?: number;
    files?: number;
    views?: number;
  };
}

@Component({
  selector: 'app-usecase-card',
  standalone: true,
  imports: [CommonModule /* , CardShareButtonComponent */], // Share feature: uncomment to re-enable
  templateUrl: './usecase-card.component.html',
  styleUrl: './usecase-card.component.scss',
  inputs: ['card', 'showShortDescription', 'showActivityMetrics'],
})
export class UsecaseCardComponent {
  @Input() card!: UsecaseCardData;
  /** When true, list view layout: label left + activity right, then arrow right on second row. */
  @Input() showShortDescription: boolean = false;
  /** When true, activity metrics (likes, downloads, views) are shown. Set false e.g. on Features of MahaAgX landing. */
  @Input() showActivityMetrics: boolean = true;
  @Input() isBookmarked: boolean = false;
  @Input() isLiked: boolean = false;
  @Input() isLoggedIn: boolean = false;
  @Output() bookmarkChanged = new EventEmitter<boolean>();
  @Output() likeChanged = new EventEmitter<boolean>();

  private router = inject(Router);
  private userInteractionApiService = inject(UserInteractionApiService);

  getBadgeClass(): string {
    if (!this.card.badgeColor) return 'usecase-card__badge--public';
    return `usecase-card__badge--${this.card.badgeColor}`;
  }

  onCardClick(): void {
    console.log('Usecase card clicked:', this.card);
    if (this.card?.id) {
      console.log('Navigating to usecase detail page with ID:', this.card.id);
      this.router.navigate(['/usecase', this.card.id]).then(
        (success) => {
          if (success) {
            console.log('Navigation successful');
          } else {
            console.error('Navigation failed');
          }
        },
        (error) => {
          console.error('Navigation error:', error);
        }
      );
    } else if (this.card?.link) {
      console.log('Navigating using link:', this.card.link);
      this.router.navigateByUrl(this.card.link);
    } else {
      console.warn('Cannot navigate: card has no ID or link', this.card);
    }
  }

  async onBookmarkIconClick(event: Event): Promise<void> {
    event.stopPropagation();
    if (!this.isLoggedIn || !this.card?.id) return;
    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.card.id,
        assetType: 'USECASE',
        actionType,
      });
      this.isBookmarked = !this.isBookmarked;
      this.bookmarkChanged.emit(this.isBookmarked);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  async onLikeIconClick(event: Event): Promise<void> {
    event.stopPropagation();
    if (!this.isLoggedIn || !this.card?.id) return;
    try {
      const actionType = this.isLiked ? 'DISLIKE' : 'LIKE';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.card.id,
        assetType: 'USECASE',
        actionType,
      });
      this.isLiked = !this.isLiked;
      this.likeChanged.emit(this.isLiked);
      if (this.card.metrics) {
        this.card.metrics.likes = this.isLiked
          ? (this.card.metrics.likes || 0) + 1
          : Math.max(0, (this.card.metrics.likes || 0) - 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }
}
